import { Avatar, Box, Card, IconButton, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { MdOutlineLogout } from "react-icons/md";
import { useHistory, useLocation } from "react-router-dom";

import logoMMImg from "../../assets/logo_MM.png";
import aclIcon from "../../assets/styled-icons/sidebar/acesscontrol.svg";
import colapseIcon from "../../assets/styled-icons/sidebar/colapse.png";
import customerIcon from "../../assets/styled-icons/sidebar/customer.svg";
import partnerIcon from "../../assets/styled-icons/sidebar/partners.svg";
import projectIcon from "../../assets/styled-icons/sidebar/project.svg";
import reportIcon from "../../assets/styled-icons/sidebar/reports.svg";
import routinesIcon from "../../assets/styled-icons/sidebar/routines.svg";
import showIcon from "../../assets/styled-icons/sidebar/show.png";
import dashIcon from "../../assets/styled-icons/sidebar/smart-home.svg";
import homeIcon from "../../assets/styled-icons/sidebar/smart-home.png";
import { AuthContext } from "../../contexts/auth";
import { RoleUserMap } from "../../types/user.type";
import { stringAvatar } from "../../utils/string.utils";
import { Item } from "./Item";
import {
  Container,
  Footer,
  Header,
  LogoDiv,
  MainContent,
  StyledIcon,
  UserInfo,
} from "./styles";
import { CollapsedItem } from "./CollapsedItem";
import { useProfile } from "../../queries/hooks";

export const options = [
  {
    // Tela inicial de todos os perfis: não depende de módulo no Controle de acesso
    title: "Início",
    icon: <img src={homeIcon} alt="Icon" />,
    isActive: false,
    route: "/inicio",
    submenus: [],
    alwaysVisible: true,
  },
  {
    title: "Dashboard",
    icon: <img src={dashIcon} alt="Icon" />,
    isActive: false,
    route: "/dashboard",
    submenus: [
      {
        title: "Controle de Eventos",
        route: "/dashboard",
      },
      {
        title: "Orçamentos",
        route: "/dashboard/orcamentos",
      },
      {
        title: "Tempo e Eficiência",
        route: "/dashboard/tempo-eficiencia",
      },
      {
        title: "Blacklist",
        route: "/dashboard/orcamentos/blacklist",
        adminOnly: true,
      },
    ],
  },
  {
    title: "Projetos",
    icon: <img src={projectIcon} alt="Icon" />,
    isActive: false,
    route: "/projetos",
    submenus: [],
  },
  {
    title: "Parceiros",
    icon: <img src={partnerIcon} alt="Icon" />,
    isActive: false,
    route: "/partners",
    submenus: [
      {
        title: "Clientes",
        route: "/partners/customers",
      },
      {
        title: "Fornecedores",
        route: "/partners/suppliers",
      },
    ],
  },
  {
    title: "Relatórios",
    icon: <img src={reportIcon} alt="Icon" />,
    isActive: false,
    route: "/reports",
    submenus: [],
  },
  {
    title: "Controle de acesso",
    icon: <img src={aclIcon} alt="Icon" />,
    isActive: false,
    route: "/access-control",
    submenus: [],
  },
  {
    title: "Rotinas",
    icon: <img src={routinesIcon} alt="Icon" />,
    isActive: false,
    route: "/routines",
    submenus: [],
    adminOnly: true,
  },
];

export default function SideBar() {
  const location = useLocation();
  const history = useHistory();
  const isHml = process.env.REACT_APP_HOST?.includes("hml");

  const { accessOptions, userRoleName } = useContext(AuthContext);
  const [optionsState, setOptionsState] = useState(options);
  const { user, signOut } = useContext(AuthContext);
  const [sidebarView, setSideBarView] = useState(true);

  // Verificar se o usuário é administrador (por role_id ou pelo nome do perfil)
  const isAdmin =
    user?.role_id === "USER_ADMINISTRATOR" || userRoleName === "Administrador";

  const { data } = useProfile();

  function handleShowSubMenus(target: string) {
    const updatedOptions = optionsState.map((el) => {
      if (el.title === target) el.isActive = true;
      else el.isActive = false;
      return el;
    });
    setOptionsState(updatedOptions);
  }

  useEffect(() => {
    if (location.pathname === "/profile") {
      handleShowSubMenus("");
      return;
    }
    const target = location.pathname.includes("partners")
      ? "Parceiros"
      : optionsState.find(
          (el) =>
            el.route === location.pathname ||
            el.submenus?.some((submenu) => submenu.route === location.pathname)
        )?.title;
    handleShowSubMenus(target || "Projetos");
  }, [location.pathname]);

  // Submenus marcados como adminOnly (ex.: Blacklist) só aparecem para administradores
  function filterSubmenus(submenus: any[] | undefined) {
    return (submenus || []).filter(
      (submenu: any) => !submenu.adminOnly || isAdmin
    );
  }

  function renderOption(item: any, index: number) {
    // Verificar se o item é adminOnly e o usuário não é admin
    if (item.adminOnly && !isAdmin) {
      return null;
    }

    const accessForItem = accessOptions.find(
      (option: any) => option.name === item.title
    );

    // Para itens adminOnly, não verificamos accessOptions se não existir
    if (
      !item.adminOnly &&
      !item.alwaysVisible &&
      (!accessForItem || !accessForItem.type.view.active)
    )
      return null;

    const submenus = filterSubmenus(item.submenus);
    const hasSubmenu = !!submenus.length;

    return (
      <Item
        key={index}
        icon={
          <StyledIcon isActive={hasSubmenu ? false : item.isActive}>
            {item.icon}
          </StyledIcon>
        }
        title={item.title}
        isActive={item.isActive}
        submenus={submenus}
        route={item.route}
        onClick={handleShowSubMenus}
      />
    );
  }

  function renderOptionCollapsed(item: any, index: number) {
    // Verificar se o item é adminOnly e o usuário não é admin
    if (item.adminOnly && !isAdmin) {
      return null;
    }

    const accessForItem = accessOptions.find(
      (option: any) => option.name === item.title
    );

    // Para itens adminOnly, não verificamos accessOptions se não existir
    if (
      !item.adminOnly &&
      !item.alwaysVisible &&
      (!accessForItem || !accessForItem.type.view.active)
    )
      return null;

    return (
      <CollapsedItem
        key={index}
        icon={<StyledIcon isActive={item.isActive}>{item.icon}</StyledIcon>}
        title={item?.title}
        isActive={item.isActive}
        submenus={filterSubmenus(item.submenus)}
        route={item.route}
        onClick={handleShowSubMenus}
      />
    );
  }

  function renderToScreen() {
    return sidebarView ? (
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          width: 215,
          minWidth: 215,
          p: 2,
          boxShadow: "0px 2px 4px 0px #A5A3AE4D",
        }}
      >
        <Header>
          <Box display={"flex"} flexDirection={"column"}>
            <LogoDiv>
              <img src={logoMMImg} alt="Logo" />
              <strong>MMIDD</strong>
            </LogoDiv>
            <Box>{isHml && <span>Homologação</span>}</Box>
          </Box>

          <img
            src={colapseIcon}
            alt="Colapse"
            onClick={() => {
              setSideBarView(!sidebarView);
            }}
          />
        </Header>

        <MainContent>
          {optionsState.map((item, index) => renderOption(item, index))}
        </MainContent>

        <Footer>
          <Avatar
            {...stringAvatar(user.name, !!data?.image_url)}
            src={data?.image_url ? data?.image_url : undefined}
            onClick={() =>
              history.push(
                isAdmin
                  ? {
                      pathname: "/access-control",
                      state: { goToProfile: Date.now() },
                    }
                  : "/profile"
              )
            }
            sx={{
              cursor: "pointer",
              img: {
                objectFit: "cover",
              },
            }}
          />

          <UserInfo>
            <Typography
              variant="body1"
              color={"#000"}
              sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
            >
              {user.name}
            </Typography>
            <Typography variant="caption" color={"#000"}>
              {RoleUserMap[user.role_id]}
            </Typography>
          </UserInfo>

          <IconButton onClick={signOut}>
            <MdOutlineLogout size={20} />
          </IconButton>
        </Footer>
      </Card>
    ) : (
      <Container>
        <Header
          style={{
            marginTop: 28,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <img src={logoMMImg} alt="Logo" />
          {isHml && (
            <Box>
              <span>HML</span>
            </Box>
          )}
          <img
            src={showIcon}
            alt="Colapse"
            onClick={() => {
              setSideBarView(!sidebarView);
            }}
          />
        </Header>
        <div style={{ marginTop: 24, flex: 1 }}>
          {optionsState.map((item, index) =>
            renderOptionCollapsed(item, index)
          )}
        </div>

        <Footer style={{ marginBottom: 8 }}>
          <Avatar
            {...stringAvatar(user.name, !!data?.image_url)}
            src={data?.image_url ? data?.image_url : undefined}
            onClick={() =>
              history.push(
                isAdmin
                  ? {
                      pathname: "/access-control",
                      state: { goToProfile: Date.now() },
                    }
                  : "/profile"
              )
            }
            sx={{
              cursor: "pointer",
              img: {
                objectFit: "cover",
              },
            }}
          />
        </Footer>
      </Container>
    );
  }

  return <>{renderToScreen()}</>;
}
